import { useEffect, useState } from "react";
import { Button, FormField, Input } from "@/design-system/components/ui";

export const STALE_CONTACT_DAYS_MIN = 1;
export const STALE_CONTACT_DAYS_MAX = 365;

/**
 * D10 — "Stale after N days", the one allocation-policy value that is NOT in
 * the shared editor modal.
 *
 * <p>It is authored on the GLOBAL row and on a TEAM tier only; the server
 * refuses it on a specialist override (400), because nothing ever reads it
 * there. So it renders on exactly those two cards, as its own small form
 * beside the shared editor rather than inside it — the editor is also the
 * override's editor, and a field that must not appear on one of the three
 * layers does not belong in a modal all three share.</p>
 *
 * <p>Blank means INHERIT, never zero, the same tri-state rule the rest of the
 * policy follows; the range is the server's (1..365) and is enforced here only
 * so a request is well-formed. The effective value is read-only prose where the
 * layer inherits, so a manager can see what their team actually runs on without
 * it being offered as something typed here.</p>
 */
export function StaleContactDaysField({
  authored,
  effective,
  canEdit,
  saving = false,
  onSave,
  testIdPrefix,
}: {
  /** What this layer authored; null/undefined inherits. */
  authored: number | null | undefined;
  /** What the layer resolves to. Undefined when the wire does not carry it
   *  (the global row has no separate resolved value) — then inheriting reads
   *  as "platform default" rather than a number nobody sent. */
  effective?: number;
  canEdit: boolean;
  saving?: boolean;
  onSave: (days: number | null) => void;
  testIdPrefix: string;
}) {
  const [draft, setDraft] = useState(authored?.toString() ?? "");
  // Re-seed on a refetch so a save elsewhere does not leave a stale draft.
  useEffect(() => {
    setDraft(authored?.toString() ?? "");
  }, [authored]);

  const parsed = parse(draft);
  const invalid = parsed === "invalid";
  const dirty = (parsed === "invalid" ? null : parsed) !== (authored ?? null) && !invalid;

  const effectiveText =
    authored != null
      ? `${authored} days (set here)`
      : effective != null
        ? `${effective} days (inherited)`
        : "Platform default (inherited)";

  return (
    <div className="flex flex-wrap items-end gap-space-3" data-testid={`${testIdPrefix}-stale-days`}>
      <div className="flex flex-wrap items-baseline gap-space-2">
        <span className="text-caption uppercase tracking-wide text-gray-mid">Stale after</span>
        <span className="text-onyx" data-testid={`${testIdPrefix}-stale-days-effective`}>
          {effectiveText}
        </span>
      </div>
      {canEdit && (
        <form
          className="flex items-end gap-space-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!invalid) onSave(parsed);
          }}
        >
          <FormField
            label="Days (blank inherits)"
            error={invalid ? `Enter a whole number from ${STALE_CONTACT_DAYS_MIN} to ${STALE_CONTACT_DAYS_MAX}.` : undefined}
          >
            <Input
              type="number"
              min={STALE_CONTACT_DAYS_MIN}
              max={STALE_CONTACT_DAYS_MAX}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="w-28"
              data-testid={`${testIdPrefix}-stale-days-input`}
            />
          </FormField>
          <Button
            type="submit"
            size="sm"
            variant="secondary"
            disabled={saving || invalid || !dirty}
            data-testid={`${testIdPrefix}-stale-days-save`}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </form>
      )}
    </div>
  );
}

/** Blank → inherit (null); a whole number in range → that; anything else →
 *  "invalid", which never reaches the save path. */
export function parse(raw: string): number | null | "invalid" {
  if (raw.trim() === "") return null;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < STALE_CONTACT_DAYS_MIN || n > STALE_CONTACT_DAYS_MAX) return "invalid";
  return n;
}
